import { Box, CircularProgress, Grid, Theme, Typography } from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/styles';
import React, { useMemo } from 'react';
import { BillingHistory } from './BillingHistory';
import { BillingOverview } from './BillingOverview';
import { BillingPlan } from './BillingPlan';
import { BillingMethod } from './BillingMethod';
import { useCampfireQuery } from '../../../../../global/network/useCampfireQuery';
import { getTenantBilling } from './__generated__/getTenantBilling';
import { GET_TENANT_BILLING } from './tenant-billing-model.gql';
import { useOrgInfo } from '../../../../../global/auth/use-org-info/use-org-info';
import { getBillingCurrency, getTaxLabel, isBillingCurrency } from './billing-currency';

export const useCommonStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      paddingLeft: 40,
      paddingRight: 40,
      paddingTop: 20,
      backgroundColor: '#F9F9F9',
      minHeight: 'calc(100% - 20px)',
    },
    heading: {
      alignItems: 'center',
      paddingBottom: '8px',
      borderBottom: '1px solid #efefef',
    },
    headingTitle: {
      color: theme.color.grey.neutralBrand900,
      fontWeight: 600,
      paddingBottom: '5px',
      fontSize: 20,
    },
    headingDescription: {
      fontSize: '14px',
      color: '#808080',
    },
    contentContainer: {
      background: 'white',
      paddingTop: '1rem',
      paddingBottom: '1.875rem',
      paddingLeft: '1.875rem',
      paddingRight: '1.875rem',
      marginTop: '1rem',
      borderRadius: '4px',
    },
    sectionHeading: {
      color: theme.color.grey.neutralBrand900,
      fontWeight: 600,
      fontSize: 16,
      paddingBottom: 8,
    },
    gridItem: {
      paddingRight: '2rem',
    },
    overviewItemHeading: {
      color: theme.color.grey.neutralBrand500,
      fontWeight: 400,
      fontSize: 14,
    },
    overviewItemData: {
      color: theme.color.grey.neutralBrand900,
      fontWeight: 600,
      fontSize: 18,
    },
  })
);

export const AdminConsoleBilling = () => {
  const classes = useCommonStyles();
  const { data, loading, error } = useCampfireQuery<getTenantBilling, {}>(GET_TENANT_BILLING);
  const orgInfo = useOrgInfo();

  const taxLabel = getTaxLabel(data?.orgRegionCode);

  const currency = useMemo(() => {
    const subscriptionJson = data?.tm.tenant.billing?.subscription;
    const subscriptionCurrency = subscriptionJson ? JSON.parse(subscriptionJson)?.currency : undefined;
    return isBillingCurrency(subscriptionCurrency) ? subscriptionCurrency : getBillingCurrency(data?.orgRegionCode);
  }, [data]);

  // Check if we should show payment method alerts (not in trial or trial expiring soon)
  const shouldAlertPaymentMethod = useMemo(() => {
    if (!orgInfo) return false;

    // If not in trial, should alert if no payment method
    if (!orgInfo.inTrial) {
      return true;
    }

    // If in trial, check if expiring within 5 days
    if (orgInfo.dateDeactivated) {
      const daysUntilExpiration = orgInfo.dateDeactivated.diffNow('days').days;
      return daysUntilExpiration <= 5 && daysUntilExpiration > 0;
    }

    return false;
  }, [orgInfo]);
  const upcoming = useMemo(() => {
    const s = data?.tm.tenant.billing?.upcoming;
    if (!s) {
      return undefined;
    }
    return JSON.parse(s);
  }, [data]);

  const invoices = useMemo(() => {
    const s = data?.tm.tenant.billing?.invoices;
    if (!s) {
      return [];
    }
    const allInvoices = JSON.parse(s)?.data || ([] as any[]);
    // Filter to show only paid or failed invoices, hide draft invoices
    return allInvoices.filter(
      (invoice: any) =>
        invoice.status === 'paid' ||
        invoice.status === 'open' ||
        invoice.status === 'past_due' ||
        invoice.status === 'uncollectible'
    );
  }, [data]);

  const paymentMethods = useMemo(() => {
    const c = data?.tm.tenant.billing?.cards;
    if (!c) {
      return [];
    }
    return JSON.parse(c)?.data || ([] as any[]);
  }, [data]);

  // Stripe does not guarantee ordering, so prefer whichever method actually gets charged.
  const defaultPaymentMethod = useMemo(() => {
    if (paymentMethods.length === 0) {
      return null;
    }

    const subscriptionJson = data?.tm.tenant.billing?.subscription;
    const customerJson = data?.tm.tenant.billing?.customer;
    const subscription = subscriptionJson ? JSON.parse(subscriptionJson) : undefined;
    const customer = customerJson ? JSON.parse(customerJson) : undefined;

    const defaultId =
      subscription?.['default_payment_method'] ??
      customer?.['invoice_settings']?.['default_payment_method'] ??
      undefined;

    return paymentMethods.find((pm: any) => pm.id === defaultId) ?? paymentMethods[0];
  }, [data, paymentMethods]);

  const product = useMemo(() => {
    const p = data?.tm.tenant.billing?.packageOption?.billingPackage.stripeProduct;
    if (!p) {
      return undefined;
    }
    // Handle both JSON objects and plain product ID strings
    try {
      return JSON.parse(p);
    } catch {
      // If it's not valid JSON, treat it as a product ID string
      return { id: p, name: 'Volaby Plan' };
    }
  }, [data]);

  const price = useMemo(() => {
    const p = data?.tm.tenant.billing?.packageOption?.stripePrice;
    if (!p) {
      return undefined;
    }
    try {
      return JSON.parse(p);
    } catch {
      // If it's not valid JSON, return a basic price object
      return { unitAmount: 0, recurring: { interval: 'month' } };
    }
  }, [data]);

  // Check for Stripe customer errors (invalid customer ID)
  const hasStripeCustomerError = error?.graphQLErrors?.some(
    (err) => err.message.includes('No such customer:') || err.message.includes('customer')
  );

  if (loading) {
    return (
      <Box className={classes.container} display='flex' flexDirection='column' alignItems='center'>
        <CircularProgress />
      </Box>
    );
  }

  // Handle Stripe customer configuration errors
  if (hasStripeCustomerError) {
    return (
      <Box className={classes.container}>
        <Box className={classes.heading}>
          <Typography variant='h2' className={classes.headingTitle}>
            Billing
          </Typography>
          <Typography variant={'body2'} className={classes.headingDescription}>
            Manage your subscription and billing information
          </Typography>
        </Box>

        <Box
          className={classes.contentContainer}
          display='flex'
          flexDirection='column'
          alignItems='center'
          justifyContent='center'
          style={{ minHeight: '300px', paddingTop: '1rem', paddingBottom: '1rem' }}
        >
          <Typography
            variant='body1'
            style={{
              fontWeight: 600,
              textAlign: 'center',
              fontSize: '15px',
              color: '#666',
              marginBottom: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <span role='img' aria-label='warning' style={{ marginRight: '8px' }}>
              ⚠️
            </span>
            Billing Configuration Issue
          </Typography>
          <Typography
            variant='body2'
            style={{
              color: '#6c757d',
              fontSize: '14px',
              textAlign: 'center',
              maxWidth: '500px',
              lineHeight: '1.4',
            }}
          >
            Something is wrong. Billing may be configured differently for your account or something has gone wrong.
            Please check back later or get in contact with us if the issue persists.
          </Typography>
        </Box>
      </Box>
    );
  }

  // Check if user has no billing setup (show pricing page instead)
  const hasNoBilling = !data?.tm?.tenant?.billing?.packageOption && !data?.tm?.tenant?.billing?.customPackage;
  const isComplementary = data?.tm?.tenant?.billing?.complementaryAccount;
  const isCustomPackage = data?.tm?.tenant?.billing?.customPackage;

  // If complementary, show only complementary message
  if (isComplementary) {
    return (
      <Box className={classes.container}>
        <Box className={classes.heading}>
          <Typography variant='h2' className={classes.headingTitle}>
            Billing
          </Typography>
          <Typography variant={'body2'} className={classes.headingDescription}>
            Manage your subscription and billing information
          </Typography>
        </Box>

        <Box
          className={classes.contentContainer}
          display='flex'
          flexDirection='column'
          alignItems='center'
          justifyContent='center'
          style={{ minHeight: '300px', paddingTop: '1rem', paddingBottom: '1rem' }}
        >
          <Typography
            variant='body1'
            style={{
              fontWeight: 600,
              textAlign: 'center',
              fontSize: '15px',
              color: '#666',
              marginBottom: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <span role='img' aria-label='party popper' style={{ marginRight: '8px' }}>
              🎉
            </span>
            This account is complementary!
          </Typography>
          <Typography
            variant='body2'
            style={{
              color: '#6c757d',
              fontSize: '14px',
              textAlign: 'center',
              maxWidth: '400px',
            }}
          >
            You&apos;re enjoying free access to Volaby. No charges will be applied to your account.
          </Typography>
        </Box>
      </Box>
    );
  }

  if (hasNoBilling) {
    // Import and show PricingPlans component when no billing exists
    const PricingPlansComponent = React.lazy(() =>
      import('./PricingPlans').then((module) => ({ default: module.PricingPlans }))
    );
    return (
      <React.Suspense
        fallback={
          <Box className={classes.container} display='flex' justifyContent='center'>
            <CircularProgress />
          </Box>
        }
      >
        <PricingPlansComponent />
      </React.Suspense>
    );
  }

  return (
    <Box className={classes.container}>
      <Box className={classes.heading}>
        <Typography variant='h2' className={classes.headingTitle}>
          Billing
        </Typography>
        <Typography variant={'body2'} className={classes.headingDescription}>
          Manage your subscription and billing information
        </Typography>
      </Box>

      <Box className={classes.contentContainer}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={4} className={classes.gridItem}>
            {upcoming && <BillingOverview upcoming={upcoming} invoices={invoices} />}
          </Grid>
          <Grid item xs={12} md={4} className={classes.gridItem}>
            <BillingPlan
              product={product}
              price={price}
              tier={data?.tm.tenant.billing?.packageOption?.billingPackage.tier}
              currency={currency}
              taxLabel={taxLabel}
              isCustomPackage={!!isCustomPackage}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <BillingMethod paymentMethod={defaultPaymentMethod} shouldAlert={shouldAlertPaymentMethod} />
          </Grid>
        </Grid>
      </Box>

      <Box className={classes.contentContainer}>
        <BillingHistory invoices={invoices} />
      </Box>
    </Box>
  );
};
